(() => {
  const selector = '[data-gh-reveal]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let observer;

  const show = (element) => {
    element.classList.add('is-gh-visible');
    element.dataset.ghRevealInitialized = 'true';
  };

  const getObserver = () => {
    if (observer || reducedMotion.matches || !('IntersectionObserver' in window)) return observer;

    const isMobileHomepage = document.body?.classList.contains('body-template-index')
      && window.matchMedia('(max-width: 749px)').matches;

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      threshold: isMobileHomepage ? 0.25 : 0.15,
      rootMargin: '0px 0px -7% 0px'
    });

    return observer;
  };


  let coverObserver;
  const coverMedia = new WeakSet();
  const sizeCoverImage = (image, media) => {
    if (!document.body.classList.contains('body-template-index')
      || !media.matches('.gh-story-media__item, .gh-story-media__visual') || !image.srcset) return;
    const width = media.clientWidth;
    const height = media.clientHeight;
    const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
    if (!width || !height || !Number.isFinite(ratio) || ratio <= 0) return;
    const cover = getComputedStyle(media).getPropertyValue('--gh-story-image-fit').trim() === 'cover';
    const renderedWidth = cover ? Math.max(width, height * ratio) : Math.min(width, height * ratio);
    const sizes = `${Math.ceil(renderedWidth)}px`;
    if (image.sizes !== sizes) image.sizes = sizes;
  };
  const updateCoverMedia = (media) => {
    media.querySelectorAll(':scope > img').forEach((image) => sizeCoverImage(image, media));
    media.querySelectorAll(':scope > template[data-gh-deferred-image]').forEach((template) => {
      template.content.querySelectorAll('img').forEach((image) => sizeCoverImage(image, media));
    });
  };
  const initializeCoverImages = (scope) => {
    if (!document.body.classList.contains('body-template-index')) return;
    if (!coverObserver && 'ResizeObserver' in window) {
      coverObserver = new ResizeObserver((entries) => entries.forEach((entry) => updateCoverMedia(entry.target)));
    }
    scope.querySelectorAll('.gh-story-media__item, .gh-story-media__visual').forEach((media) => {
      updateCoverMedia(media);
      if (coverObserver && !coverMedia.has(media)) {
        coverMedia.add(media);
        coverObserver.observe(media);
      }
    });
  };

  let imageObserver;
  const observedMedia = new WeakSet();
  // Eine Bildschirmhöhe Vorlauf, ohne die sofort sichtbaren Bilder zu verdrängen.
  const imagePreloadDistance = Math.max(1, Math.round(window.innerHeight));
  const loadImages = (media) => {
    const bounds = media.getBoundingClientRect();
    const isVisible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    media.querySelectorAll(':scope > template[data-gh-deferred-image]').forEach((template) => {
      const content = template.content.cloneNode(true);
      content.querySelectorAll('img').forEach((image) => {
        sizeCoverImage(image, media);
        image.loading = 'eager';
        if (!isVisible) image.fetchPriority = 'low';
      });
      template.replaceWith(content);
    });
    imageObserver?.unobserve(media);
  };

  const initializeImages = (scope) => {
    if (!imageObserver && 'IntersectionObserver' in window) {
      imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) loadImages(entry.target); });
      }, { rootMargin: `${imagePreloadDistance}px 0px`, threshold: 0 });
    }
    scope.querySelectorAll('template[data-gh-deferred-image]').forEach((template) => {
      const media = template.parentElement;
      if (!imageObserver) { loadImages(media); return; }
      if (observedMedia.has(media)) return;
      observedMedia.add(media);
      const bounds = media.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0 && bounds.bottom > -imagePreloadDistance
        && bounds.top < window.innerHeight + imagePreloadDistance && bounds.right > 0 && bounds.left < window.innerWidth) {
        loadImages(media);
      } else {
        imageObserver.observe(media);
      }
    });
  };

  const initialize = (scope = document) => {
    initializeCoverImages(scope);
    initializeImages(scope);
    const elements = Array.from(scope.querySelectorAll(selector));
    const viewportHeight = window.innerHeight;
    const initiallyVisible = new Set(elements.filter((element) => {
      const bounds = element.getBoundingClientRect();
      return bounds.height > 0 && bounds.bottom > 0 && bounds.top < viewportHeight;
    }));
    const revealObserver = getObserver();

    elements.forEach((element) => {
      if (element.dataset.ghRevealInitialized === 'true') return;
      if (reducedMotion.matches || !revealObserver || initiallyVisible.has(element)) {
        element.classList.add('is-gh-immediate');
        show(element);
        return;
      }

      element.dataset.ghRevealInitialized = 'true';
      revealObserver.observe(element);
    });
    document.documentElement.classList.add('gh-motion-ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.gh-story-media__item, .gh-story-media__visual').forEach((media) => {
      coverObserver?.unobserve(media);
      imageObserver?.unobserve(media);
      coverMedia.delete(media);
      observedMedia.delete(media);
    });
  });
})();
